/**
 * `OPS-206`: "In-product feedback capture, routed to the admin portal." (SHOULD)
 *
 * The requirement names a capture and a destination and nothing else, so every number below is a
 * decision the spec did not make, recorded here rather than buried at a call site.
 *
 * **Categories and statuses are a product call.** Four categories a shopkeeper can pick without
 * thinking, three statuses an operator moves a note through. Neither list is an Appendix E
 * registry enum, and neither is in a database CHECK: these constants are the only place they are
 * enforced (`normaliseFeedback`, the admin service), so changing a list is a code change and not
 * a migration.
 *
 * **4000 characters is an engineering bound, not a spec value.** It keeps a row and the operator
 * list a bounded size. The spec is silent, and the same number is a CHECK on the platform table so
 * a path that skips `normaliseFeedback` still cannot store more.
 */
export const FEEDBACK_CATEGORIES = ['bug', 'idea', 'question', 'praise'];
export const FEEDBACK_STATUSES = ['new', 'triaged', 'closed'];
export const FEEDBACK_MAX_LENGTH = 4000;
export class FeedbackError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'FeedbackError';
    }
}
/**
 * `new -> triaged -> closed`, forward only. A note can be closed straight from `new` (a duplicate,
 * a thank-you), and nothing moves back: a reopened conversation is a new note from the shop.
 */
export function feedbackMoveAllowed(from, to) {
    return FEEDBACK_STATUSES.indexOf(to) > FEEDBACK_STATUSES.indexOf(from);
}
/**
 * Trim, then refuse what the platform table would refuse, with a sentence naming the bound.
 *
 * The counter runs the same function before it queues the write: a refused write is retried by the
 * write queue (`OFF-001a`) with every write behind it, so the limit has to be met on the client.
 */
export function normaliseFeedback(input) {
    const category = FEEDBACK_CATEGORIES.includes(input.category)
        ? input.category
        : undefined;
    if (category === undefined) {
        throw new FeedbackError('FEEDBACK_CATEGORY_UNKNOWN', `Choose one of: ${FEEDBACK_CATEGORIES.join(', ')}.`);
    }
    const body = typeof input.body === 'string' ? input.body.trim() : '';
    if (body === '') {
        throw new FeedbackError('FEEDBACK_BODY_REQUIRED', 'Write a message before sending.');
    }
    if (body.length > FEEDBACK_MAX_LENGTH) {
        throw new FeedbackError('FEEDBACK_TOO_LONG', `A message can be at most ${FEEDBACK_MAX_LENGTH} characters. Shorten it and send again.`);
    }
    return { category, body };
}
//# sourceMappingURL=feedback.js.map